import { ENCOUNTER_MODULES, type EmrContext, type EmrFormConfigRow, type EmrFormGroup, type EmrFormRuleInput, type ModuleCode } from '@his/shared';

export function emrContextsFor(hasModule: (module: ModuleCode) => boolean): EmrContext[] {
  const contexts: EmrContext[] = [];
  if (ENCOUNTER_MODULES.some((module) => module !== 'ipd' && hasModule(module))) contexts.push('opd');
  if (hasModule('ipd')) contexts.push('ipd');
  return contexts;
}

export function emrFormsTabVisible(hasModule: (module: ModuleCode) => boolean, can: (...permissions: string[]) => boolean): boolean {
  return emrContextsFor(hasModule).length > 0 && can('emr.config.manage', 'tenant.settings.manage', 'tenant.settings.read');
}

export interface DraftRow {
  key: string;
  enabled: boolean;
  required: boolean;
  sortOrder: number;
  own: boolean;
}

export type EmrDraft = Readonly<Record<string, DraftRow>>;

export type EmrFormState = 'required' | 'optional' | 'hidden';

const GROUP_ORDER: Record<EmrContext, EmrFormGroup[]> = {
  opd: ['section', 'order', 'clinical_form'],
  ipd: ['ipd_form', 'order', 'clinical_form'],
};

export function groupsFor(context: EmrContext): EmrFormGroup[] {
  return GROUP_ORDER[context];
}

export function draftFrom(rows: readonly EmrFormConfigRow[]): EmrDraft {
  return Object.fromEntries(
    rows.map((row) => [row.key, { key: row.key, enabled: row.enabled, required: row.required, sortOrder: row.sortOrder, own: row.overridden }]),
  );
}

export function rowsInGroup(rows: readonly EmrFormConfigRow[], draft: EmrDraft, group: EmrFormGroup): EmrFormConfigRow[] {
  const position = new Map(rows.map((row, index) => [row.key, index]));
  return rows
    .filter((row) => row.group === group && draft[row.key])
    .sort((a, b) => draft[a.key].sortOrder - draft[b.key].sortOrder || (position.get(a.key) ?? 0) - (position.get(b.key) ?? 0));
}

export function withEnabled(draft: EmrDraft, key: string, enabled: boolean): EmrDraft {
  const row = draft[key];
  if (!row) return draft;
  return { ...draft, [key]: { ...row, enabled, required: enabled ? row.required : false, own: true } };
}

export function withRequired(draft: EmrDraft, key: string, required: boolean): EmrDraft {
  const row = draft[key];
  if (!row) return draft;
  return { ...draft, [key]: { ...row, required, enabled: required ? true : row.enabled, own: true } };
}

export function withMove(draft: EmrDraft, orderedKeys: readonly string[], index: number, delta: -1 | 1): EmrDraft {
  const target = index + delta;
  if (target < 0 || target >= orderedKeys.length) return draft;
  const moving = draft[orderedKeys[index]];
  const other = draft[orderedKeys[target]];
  if (!moving || !other) return draft;
  const tied = moving.sortOrder === other.sortOrder;
  const movingOrder = tied ? (delta < 0 ? other.sortOrder : other.sortOrder + 1) : other.sortOrder;
  const otherOrder = tied ? (delta < 0 ? moving.sortOrder + 1 : moving.sortOrder) : moving.sortOrder;
  return {
    ...draft,
    [moving.key]: { ...moving, sortOrder: movingOrder, own: true },
    [other.key]: { ...other, sortOrder: otherOrder, own: true },
  };
}

export function withReset(draft: EmrDraft, row: EmrFormConfigRow): EmrDraft {
  return {
    ...draft,
    [row.key]: { key: row.key, enabled: row.inherited.enabled, required: row.inherited.required, sortOrder: row.inherited.sortOrder, own: false },
  };
}

function differs(row: EmrFormConfigRow, draft: DraftRow): boolean {
  return row.enabled !== draft.enabled || row.required !== draft.required || row.sortOrder !== draft.sortOrder;
}

export function draftChanges(rows: readonly EmrFormConfigRow[], draft: EmrDraft): { rules: EmrFormRuleInput[]; reset: string[] } {
  const rules: EmrFormRuleInput[] = [];
  const reset: string[] = [];
  for (const row of rows) {
    const current = draft[row.key];
    if (!current) continue;
    if (current.own && (!row.overridden || differs(row, current))) {
      rules.push({ formKey: row.key, enabled: current.enabled, required: current.required, sortOrder: current.sortOrder });
    }
    if (!current.own && row.overridden) reset.push(row.key);
  }
  return { rules, reset };
}

export function draftIsDirty(rows: readonly EmrFormConfigRow[], draft: EmrDraft): boolean {
  const { rules, reset } = draftChanges(rows, draft);
  return rules.length > 0 || reset.length > 0;
}

export function formState(row: Pick<DraftRow, 'enabled' | 'required'>): EmrFormState {
  if (!row.enabled) return 'hidden';
  return row.required ? 'required' : 'optional';
}

export function draftSummary(rows: readonly EmrFormConfigRow[], draft: EmrDraft): { shown: number; total: number; required: number } {
  const current = rows.map((row) => draft[row.key]).filter((row): row is DraftRow => Boolean(row));
  return {
    shown: current.filter((row) => row.enabled).length,
    total: current.length,
    required: current.filter((row) => row.enabled && row.required).length,
  };
}
